import { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, TextField, CircularProgress, Stack,
} from '@mui/material';
import { MarkEmailReadRounded } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from '../utils/axios';
import useAuth from '../hooks/useAuth';

const PURPLE = '#7635dc';
const PURPLE_GRADIENT = 'linear-gradient(135deg, #7635dc 0%, #9d4edd 100%)';

// Only shown once per browser session after the user dismisses it, so a
// self-signup user is reminded to verify without being nagged on every page.
const DISMISS_KEY = 'deskos_email_verify_dismissed';

/**
 * Post-onboarding email verification prompt.
 *
 * Renders nothing unless the signed-in user is a self-serve org admin whose
 * email is still pending verification (user.pending_email_verification). Staff
 * created and pre-existing accounts never carry that flag, so they never see it.
 */
export default function EmailVerificationPrompt() {
  const { user, fetchUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const eligible = !!user && user.role === 'org' && user.pending_email_verification === true;

  const [open, setOpen] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    if (eligible && sessionStorage.getItem(DISMISS_KEY) !== '1') {
      setOpen(true);
    }
  }, [eligible]);

  useEffect(() => () => clearInterval(countRef.current), []);

  function startCountdown() {
    setCountdown(60);
    clearInterval(countRef.current);
    countRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(countRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }

  if (!eligible) return null;

  const handleSend = async () => {
    setSending(true);
    try {
      await axios.post('/api/v1/accounts/public/signup/email-verification/send/');
      setCodeSent(true);
      startCountdown();
      enqueueSnackbar('Verification code sent to your email', { variant: 'success' });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Could not send the code. Please try again.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (otp.length !== 6) {
      enqueueSnackbar('Enter the 6-digit code', { variant: 'warning' });
      return;
    }
    setVerifying(true);
    try {
      await axios.post('/api/v1/accounts/public/signup/email-verification/confirm/', { otp });
      enqueueSnackbar('Email verified. Thank you!', { variant: 'success' });
      setOpen(false);
      // Refresh the cached user so the flag clears and the prompt stops showing.
      try { await fetchUser(); } catch (_) { /* non-fatal */ }
    } catch (err) {
      const msg = err?.response?.data?.error || 'Verification failed';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setVerifying(false);
    }
  };

  const handleLater = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleLater}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
        <Box
          sx={{
            width: 56, height: 56, borderRadius: '18px', mx: 'auto', mb: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: PURPLE_GRADIENT,
            boxShadow: '0 14px 30px -12px rgba(118,53,220,0.7)',
          }}
        >
          <MarkEmailReadRounded sx={{ color: '#fff', fontSize: 28 }} />
        </Box>
        <Typography variant="h5" fontWeight={800}>
          Verify your email
        </Typography>
      </DialogTitle>

      <DialogContent>
        {!codeSent ? (
          <Typography variant="body2" color="text.secondary" align="center">
            Confirm <strong>{user.email}</strong> so you never lose access to your
            workspace and can receive important account emails.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary" align="center">
              We sent a 6-digit code to <strong>{user.email}</strong>. Enter it below.
            </Typography>
            <TextField
              fullWidth
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => e.key === 'Enter' && handleVerify()}
              placeholder="000000"
              inputProps={{ maxLength: 6, inputMode: 'numeric', style: { textAlign: 'center', letterSpacing: 10, fontSize: 24, fontWeight: 700 } }}
            />
            <Box sx={{ textAlign: 'center' }}>
              {countdown > 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Resend code in {countdown}s
                </Typography>
              ) : (
                <Button size="small" onClick={handleSend} disabled={sending} sx={{ color: PURPLE, textTransform: 'none', fontWeight: 700 }}>
                  {sending ? 'Sending...' : 'Resend code'}
                </Button>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, flexDirection: 'column', gap: 1 }}>
        {!codeSent ? (
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleSend}
            disabled={sending}
            sx={{ py: 1.25, borderRadius: 2, fontWeight: 700, textTransform: 'none', background: PURPLE_GRADIENT, '&:hover': { background: 'linear-gradient(135deg, #6a2cc9 0%, #8a3fc9 100%)' } }}
          >
            {sending ? <CircularProgress size={22} color="inherit" /> : 'Send verification code'}
          </Button>
        ) : (
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleVerify}
            disabled={verifying || otp.length !== 6}
            sx={{ py: 1.25, borderRadius: 2, fontWeight: 700, textTransform: 'none', background: PURPLE_GRADIENT, '&:hover': { background: 'linear-gradient(135deg, #6a2cc9 0%, #8a3fc9 100%)' } }}
          >
            {verifying ? <CircularProgress size={22} color="inherit" /> : 'Verify email'}
          </Button>
        )}
        <Button fullWidth onClick={handleLater} sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}>
          I&apos;ll do this later
        </Button>
      </DialogActions>
    </Dialog>
  );
}
